import { z } from 'zod';
import { Admission } from '../schemas/admission.ts';
import { ChatMessage, PlaybackState } from '../schemas/room.ts';
import { ScreeningPublic } from '../schemas/screenings.ts';

/** Data-channel topic for server → client events. Clients never publish on it. */
export const REALTIME_TOPIC = 'seans.v1';

/**
 * Server → room events, published reliably by the server through LiveKit. Clients apply them on
 * top of the room snapshot: playback by `version`, chat by `id` (dedupe), screening by replacing it
 * (and refetching the snapshot when `assetId` changed). Admission events go only to hosts.
 */
export const RealtimeEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('playback.updated'), playback: PlaybackState }),
  z.object({ type: z.literal('chat.created'), message: ChatMessage }),
  z.object({ type: z.literal('screening.updated'), screening: ScreeningPublic }),
  z.object({ type: z.literal('participant.removed'), identity: z.string(), banned: z.boolean() }),
  z.object({ type: z.literal('admission.requested'), admission: Admission }),
  z.object({ type: z.literal('admission.decided'), admission: Admission }),
]);
export type RealtimeEvent = z.infer<typeof RealtimeEvent>;

export const RealtimeEnvelope = z.object({ v: z.literal(1), event: RealtimeEvent });
export type RealtimeEnvelope = z.infer<typeof RealtimeEnvelope>;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export const encodeRealtimeEvent = (event: RealtimeEvent): Uint8Array =>
  encoder.encode(JSON.stringify({ v: 1, event } satisfies RealtimeEnvelope));

/** Returns null for payloads that are not valid events (unknown versions, foreign topics). */
export const decodeRealtimeEvent = (payload: Uint8Array): RealtimeEvent | null => {
  try {
    const parsed = RealtimeEnvelope.safeParse(JSON.parse(decoder.decode(payload)));
    return parsed.success ? parsed.data.event : null;
  } catch {
    return null;
  }
};
